/**
 * 직원 상세 - 인적사항 탭 (V62 안정화)
 *
 * 편집 가능 필드:
 * - nameKo (MID), gender (MID), birthDate (MID), nationalityType (MID)
 * - affiliationName (LOW) — 파견직만 자유 입력, 나머지는 '엔글' 고정
 *
 * 편집 불가 필드:
 * - employeeNumber (BLOCKED — 자동채번 전용)
 * - email (BLOCKED)
 *
 * V70.9: 주민등록번호 수정
 * - 대상: **KR 소속 직원만** (CN 공민신분번호는 이번 범위 제외)
 * - 경로: 일반 프로필 callable이 아니라 민감정보 전용 `updateEmployeeSensitiveInfo`
 *   (updateEmployeeFullProfileSafe 의 residentRegistrationNo = 'BLOCKED' 정책은 그대로 유지)
 * - 값이 실제로 바뀔 때만 변경 사유 필수 + 서버에서 감사로그 기록
 * - 여기 권한 표시는 UX 용이며 최종 판정은 서버가 한다
 */

import React, { useState, useCallback } from 'react';
import { User, Edit2, Save, X } from 'lucide-react';
import { Employee } from '../../../../shared/types/employee';
import { EmployeeSensitiveInfo } from '../../../../shared/hooks/useEmployeeSensitiveInfo';
import {
  useEmployeeFullProfileSafeMutations,
  diffChanges,
  filterFieldsByRisk,
} from '../../../../shared/hooks/useEmployeeFullProfileSafeMutations';
import { useEmployeeSensitiveInfoMutations } from '../../../../shared/hooks/useEmployeeSensitiveInfoMutations';

/**
 * V70.9: 주민등록번호 정규화 (BE `normalizeRegistrationNo`와 동일 규칙)
 *  - 하이픈 제거 후 13자리면 `XXXXXX-XXXXXXX`, 아니면 입력 그대로
 *  - 변경 여부 판정 용도이며 저장 형식의 SSoT는 서버다
 */
function normalizeRrn(value: string | null | undefined): string {
  if (!value) return '';
  const cleaned = String(value).replace(/-/g, '').trim();
  if (cleaned.length === 13) return `${cleaned.substring(0, 6)}-${cleaned.substring(6)}`;
  return String(value).trim();
}

/** V70.9: 형식 검증 (BE `validateRegistrationNoFormat`과 동일 — 13자리 숫자) */
function isValidRrnFormat(value: string): boolean {
  return /^\d{13}$/.test(String(value || '').replace(/-/g, ''));
}

/**
 * V70.9: 주민등록번호에서 생년월일 유도 (KR 규칙, **경고 표시 전용**)
 *  - 저장 값에는 영향을 주지 않으며 birthDate/gender를 자동 변경하지 않는다
 *  - 서버는 이 불일치를 이유로 저장을 막지 않는다 (기존 데이터에 불일치가 존재)
 */
function deriveBirthDateFromRrn(value: string): string | null {
  const cleaned = String(value || '').replace(/-/g, '').trim();
  if (cleaned.length < 7) return null;
  const genderDigit = parseInt(cleaned.charAt(6), 10);
  let century: string;
  if ([1, 2, 5, 6].includes(genderDigit)) century = '19';
  else if ([3, 4, 7, 8].includes(genderDigit)) century = '20';
  else if ([9, 0].includes(genderDigit)) century = '18';
  else return null;
  const mm = cleaned.substring(2, 4);
  const dd = cleaned.substring(4, 6);
  const month = parseInt(mm, 10);
  const day = parseInt(dd, 10);
  if (!(month >= 1 && month <= 12) || !(day >= 1 && day <= 31)) return null;
  return `${century}${cleaned.substring(0, 2)}-${mm}-${dd}`;
}

interface PersonalInfoTabProps {
  employee: Employee;
  sensitiveInfo?: EmployeeSensitiveInfo | null;
  canEdit?: boolean;
  onRefresh?: () => void;
}

export function PersonalInfoTab({ employee, sensitiveInfo, canEdit, onRefresh }: PersonalInfoTabProps) {
  const registrationNumber = sensitiveInfo?.residentRegistrationNumber || employee.residentRegistrationNumber;
  const isDispatch = employee.currentEmploymentTypeCode === 'DISPATCH';

  // 편집 상태
  const [isEditing, setIsEditing] = useState(false);
  const [editedValues, setEditedValues] = useState<Record<string, any>>({});
  const { updateFullProfile, isLoading } = useEmployeeFullProfileSafeMutations();
  // V70.9: 주민등록번호는 민감정보 전용 callable 사용
  const { updateEmployeeSensitiveInfo, isLoading: isSavingRrn } = useEmployeeSensitiveInfoMutations();

  /** V70.9: 주민등록번호 수정 가능 여부 — 수정 권한 + KR 소속 직원 (최종 판정은 서버) */
  const canEditRrn = !!canEdit && employee.companyCode === 'KR';

  const handleEdit = useCallback(() => {
    setEditedValues({
      nameKo: employee.nameKo || '',
      birthDate: employee.birthDate || '',
      gender: employee.gender || '',
      nationalityType: employee.nationalityType || '',
      affiliationName: employee.affiliationName || '',
      // V70.9: 주민등록번호 + 변경 사유 (프로필 필드와 분리해서 저장한다)
      residentRegistrationNo: registrationNumber || '',
      rrnReason: '',
    });
    setIsEditing(true);
  }, [employee, registrationNumber]);

  const handleCancel = useCallback(() => {
    setIsEditing(false);
    setEditedValues({});
  }, []);

  const handleFieldChange = useCallback((field: string, value: any) => {
    setEditedValues((prev) => ({ ...prev, [field]: value }));
  }, []);

  const handleSave = useCallback(async () => {
    const original: Record<string, any> = {
      nameKo: employee.nameKo || '',
      birthDate: employee.birthDate || '',
      gender: employee.gender || '',
      nationalityType: employee.nationalityType || '',
      affiliationName: employee.affiliationName || '',
    };
    // V70.9: 주민등록번호/사유는 프로필 callable 대상이 아니므로 분리한다
    const { residentRegistrationNo: editedRrn, rrnReason, ...profileEdited } = editedValues;
    const changes = diffChanges(original, profileEdited);

    // ── V70.9: 주민등록번호 변경 여부 (정규화 후 비교)
    const originalRrn = normalizeRrn(registrationNumber);
    const nextRrn = normalizeRrn(editedRrn);
    const rrnChanged = canEditRrn && nextRrn !== originalRrn;

    if (Object.keys(changes).length === 0 && !rrnChanged) {
      alert('변경된 내용이 없습니다.');
      return;
    }

    // ── V70.9: 주민등록번호 사전 검증 (서버가 최종 판정하지만 UX상 먼저 막는다)
    if (rrnChanged) {
      if (!nextRrn) {
        alert('주민등록번호는 빈 값으로 저장할 수 없습니다.');
        return;
      }
      if (!isValidRrnFormat(nextRrn)) {
        alert('주민등록번호 형식이 올바르지 않습니다. (13자리 숫자)');
        return;
      }
      if (!String(rrnReason || '').trim()) {
        alert('주민등록번호를 변경하려면 변경 사유를 입력해야 합니다.');
        return;
      }
      // 생년월일 불일치 경고 — 확인 시에도 birthDate/gender 는 변경하지 않는다
      const derived = deriveBirthDateFromRrn(nextRrn);
      const currentBirth = (changes.birthDate ?? employee.birthDate) || '';
      if (derived && currentBirth && derived !== currentBirth) {
        if (
          !window.confirm(
            '입력한 주민등록번호의 생년월일과 현재 등록된 생년월일이 일치하지 않습니다. 그래도 수정하시겠습니까?\n\n' +
              '(생년월일·성별은 자동으로 변경되지 않습니다)'
          )
        ) {
          return;
        }
      }
    }

    // MID 필드 확인 팝업
    const midFields = filterFieldsByRisk(changes, 'MID');
    if (midFields.length > 0) {
      const fieldLabels: Record<string, string> = {
        nameKo: '이름', gender: '성별', birthDate: '생년월일', nationalityType: '내/외국인',
      };
      const labels = midFields.map((f) => fieldLabels[f] || f).join(', ');
      if (!window.confirm(`다음 필드를 수정합니다: ${labels}\n\n계속하시겠습니까?`)) {
        return;
      }
    }

    // 저장 실행 (HIGH 필드 없으므로 사유 모달 불필요)
    const updatedAt = employee.updatedAt instanceof Date ? employee.updatedAt.toISOString() : undefined;

    // ── V70.9: 저장 순서 = 민감정보(주민번호) → 일반 프로필
    //   두 callable 사이에는 원자성이 없다. 더 엄격한 전제(권한/회사scope/사유/중복/동시수정)를
    //   가진 주민번호를 먼저 시도해, 실패 시 **아무것도 바뀌지 않은 상태**로 끝나게 한다.
    //   주민번호 저장이 users.updatedAt 을 갱신하므로, 이어지는 프로필 저장에는
    //   서버가 돌려준 updatedAtHint 를 사용해 동시수정 가드가 오탐하지 않게 한다.
    let effectiveUpdatedAt = updatedAt;
    if (rrnChanged) {
      try {
        const res = await updateEmployeeSensitiveInfo({
          employeeId: employee.id,
          residentRegistrationNo: nextRrn,
          reason: String(rrnReason || '').trim(),
          currentUpdatedAt: updatedAt,
        });
        if (res?.updatedAtHint) {
          effectiveUpdatedAt = res.updatedAtHint;
        }
      } catch (err: any) {
        alert(`주민등록번호 저장 실패: ${err?.message || '알 수 없는 오류'}`);
        onRefresh?.();
        return;
      }
    }

    if (Object.keys(changes).length === 0) {
      setIsEditing(false);
      setEditedValues({});
      onRefresh?.();
      return;
    }

    const success = await updateFullProfile(employee.id, changes, undefined, effectiveUpdatedAt);
    if (success) {
      setIsEditing(false);
      setEditedValues({});
      onRefresh?.();
    } else if (rrnChanged) {
      // 주민번호는 저장됐으나 프로필 저장이 실패한 경우 — 화면을 실제 DB 상태로 되돌린다
      alert('주민등록번호는 저장되었으나 나머지 인적사항 저장에 실패했습니다. 화면을 새로고침한 뒤 다시 시도하세요.');
      onRefresh?.();
    }
  }, [editedValues, employee, updateFullProfile, updateEmployeeSensitiveInfo, onRefresh, registrationNumber, canEditRrn]);

  return (
    <div style={containerStyle}>
      {/* 섹션 헤더 */}
      <div style={headerStyle}>
        <User size={20} style={{ color: 'var(--color-primary)' }} />
        <h3 style={titleStyle}>인적사항</h3>
        {canEdit && !isEditing && (
          <button onClick={handleEdit} style={editButtonStyle}>
            <Edit2 size={16} />
            수정
          </button>
        )}
        {isEditing && (
          <div style={{ marginLeft: 'auto', display: 'flex', gap: '8px' }}>
            <button onClick={handleCancel} style={cancelButtonStyle} disabled={isLoading}>
              <X size={16} />
              취소
            </button>
            <button onClick={handleSave} style={saveButtonStyle} disabled={isLoading}>
              <Save size={16} />
              {isLoading ? '저장 중...' : '저장'}
            </button>
          </div>
        )}
        {!canEdit && <span style={readOnlyBadgeStyle}>수정 불가</span>}
      </div>

      {/* 정보 그리드 */}
      <div style={gridStyle}>
        {/* 이름 (MID) */}
        {isEditing ? (
          <EditField label="이름 (한글)" risk="MID">
            <input
              type="text"
              value={editedValues.nameKo || ''}
              onChange={(e) => handleFieldChange('nameKo', e.target.value)}
              style={inputStyle}
            />
          </EditField>
        ) : (
          <InfoItem label="이름 (한글)" value={employee.nameKo} />
        )}

        {/* 사번 (BLOCKED) */}
        <InfoItem label="사번" value={employee.employeeNumber || '-'} mono blocked />

        {/* 이메일 (BLOCKED) */}
        <InfoItem label="이메일" value={employee.email || '-'} blocked />

        {/* 생년월일 (MID) */}
        {isEditing ? (
          <EditField label="생년월일" risk="MID">
            <input
              type="date"
              value={editedValues.birthDate || ''}
              onChange={(e) => handleFieldChange('birthDate', e.target.value)}
              style={inputStyle}
            />
          </EditField>
        ) : (
          <InfoItem label="생년월일" value={employee.birthDate || '-'} />
        )}

        {/* 성별 (MID) */}
        {isEditing ? (
          <EditField label="성별" risk="MID">
            <select
              value={editedValues.gender || ''}
              onChange={(e) => handleFieldChange('gender', e.target.value)}
              style={inputStyle}
            >
              <option value="">선택</option>
              <option value="male">남성</option>
              <option value="female">여성</option>
            </select>
          </EditField>
        ) : (
          <InfoItem
            label="성별"
            value={employee.gender === 'male' ? '남성' : employee.gender === 'female' ? '여성' : '-'}
          />
        )}

        {/* 내/외국인 (MID) */}
        {isEditing ? (
          <EditField label="내/외국인" risk="MID">
            <select
              value={editedValues.nationalityType || ''}
              onChange={(e) => handleFieldChange('nationalityType', e.target.value)}
              style={inputStyle}
            >
              <option value="">선택</option>
              <option value="domestic">내국인</option>
              <option value="foreign">외국인</option>
            </select>
          </EditField>
        ) : (
          <InfoItem
            label="내/외국인"
            value={employee.nationalityType === 'foreign' ? '외국인' : employee.nationalityType === 'domestic' ? '내국인' : '-'}
          />
        )}

        {/* 주민등록번호 — V70.9: 권한 + KR 직원일 때만 수정 가능 */}
        {isEditing && canEditRrn ? (
          <>
            <EditField label="주민등록번호" risk="HIGH">
              <input
                type="text"
                value={editedValues.residentRegistrationNo || ''}
                onChange={(e) => handleFieldChange('residentRegistrationNo', e.target.value)}
                placeholder="123456-1234567"
                maxLength={14}
                autoComplete="off"
                style={{ ...inputStyle, fontFamily: 'monospace' }}
              />
            </EditField>
            {normalizeRrn(editedValues.residentRegistrationNo) !== normalizeRrn(registrationNumber) && (
              <EditField label="주민등록번호 변경 사유 (필수)" risk="HIGH">
                <input
                  type="text"
                  value={editedValues.rrnReason || ''}
                  onChange={(e) => handleFieldChange('rrnReason', e.target.value)}
                  placeholder="예: 주민등록번호 오기재 정정"
                  style={inputStyle}
                />
              </EditField>
            )}
          </>
        ) : (
          <InfoItem
            label={employee.companyCode === 'CN' ? '공민신분번호' : '주민등록번호'}
            value={registrationNumber || '(미등록)'}
            mono
            blocked={!canEditRrn}
          />
        )}

        {/* 소속 (LOW) — 파견직만 자유 입력 */}
        {isEditing && isDispatch ? (
          <EditField label="파견회사명" risk="LOW">
            <input
              type="text"
              value={editedValues.affiliationName || ''}
              onChange={(e) => handleFieldChange('affiliationName', e.target.value)}
              placeholder="파견업체명을 입력하세요"
              style={inputStyle}
            />
          </EditField>
        ) : (
          <InfoItem label="소속" value={employee.affiliationName || '-'} />
        )}
      </div>
    </div>
  );
}

// ============================================
// 공통 컴포넌트
// ============================================

interface InfoItemProps {
  label: string;
  value: string;
  mono?: boolean;
  blocked?: boolean;
}

function InfoItem({ label, value, mono, blocked }: InfoItemProps) {
  return (
    <div>
      <div style={labelStyle}>
        {label}
        {blocked && <span style={{ marginLeft: '6px', color: 'var(--color-gray-400)', fontSize: '11px' }}>(수정 불가)</span>}
      </div>
      <div style={{ ...valueStyle, fontFamily: mono ? 'monospace' : 'inherit' }}>
        {value}
      </div>
    </div>
  );
}

interface EditFieldProps {
  label: string;
  /** V70.9: 민감정보(주민등록번호) 표기를 위해 'HIGH' 추가 */
  risk: 'LOW' | 'MID' | 'HIGH';
  children: React.ReactNode;
}

function EditField({ label, risk, children }: EditFieldProps) {
  return (
    <div>
      <div style={labelStyle}>
        {label}
        {(risk === 'MID' || risk === 'HIGH') && (
          <span style={{
            marginLeft: '6px',
            fontSize: '10px',
            fontWeight: '600',
            color: risk === 'HIGH' ? 'var(--color-error)' : 'var(--color-warning)',
            padding: '1px 6px',
            border: `1px solid ${risk === 'HIGH' ? 'var(--color-error)' : 'var(--color-warning)'}`,
            borderRadius: '4px',
          }}>
            {risk}
          </span>
        )}
      </div>
      {children}
    </div>
  );
}

// ============================================
// 스타일
// ============================================

const containerStyle: React.CSSProperties = {
  background: 'white',
  borderRadius: '12px',
  padding: '24px',
  boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
};

const headerStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  marginBottom: '20px',
  paddingBottom: '12px',
  borderBottom: '1px solid var(--color-gray-200)',
};

const titleStyle: React.CSSProperties = {
  fontSize: '18px',
  fontWeight: '600',
  color: 'var(--color-gray-800)',
  margin: 0,
};

const readOnlyBadgeStyle: React.CSSProperties = {
  marginLeft: 'auto',
  padding: '4px 12px',
  background: 'var(--color-gray-100)',
  color: 'var(--color-gray-600)',
  fontSize: '12px',
  fontWeight: '500',
  borderRadius: '12px',
};

const editButtonStyle: React.CSSProperties = {
  marginLeft: 'auto',
  display: 'flex',
  alignItems: 'center',
  gap: '6px',
  padding: '8px 16px',
  background: 'var(--color-primary)',
  color: 'white',
  border: 'none',
  borderRadius: '6px',
  fontSize: '14px',
  fontWeight: '500',
  cursor: 'pointer',
};

const saveButtonStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '6px',
  padding: '8px 16px',
  background: 'var(--color-success)',
  color: 'white',
  border: 'none',
  borderRadius: '6px',
  fontSize: '14px',
  fontWeight: '500',
  cursor: 'pointer',
};

const cancelButtonStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '6px',
  padding: '8px 16px',
  background: 'var(--color-gray-200)',
  color: 'var(--color-gray-700)',
  border: 'none',
  borderRadius: '6px',
  fontSize: '14px',
  fontWeight: '500',
  cursor: 'pointer',
};

const gridStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
  gap: '20px',
};

const labelStyle: React.CSSProperties = {
  fontSize: '12px',
  fontWeight: '500',
  color: 'var(--color-gray-500)',
  marginBottom: '4px',
};

const valueStyle: React.CSSProperties = {
  fontSize: '14px',
  color: 'var(--color-gray-800)',
  fontWeight: '500',
};

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '8px 12px',
  border: '1px solid var(--color-gray-300)',
  borderRadius: '6px',
  fontSize: '14px',
  outline: 'none',
  boxSizing: 'border-box',
};
